import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import useRetryFetch from "../../hooks/useRetryFetch";
import { liftEntity, listObjects, type MmObject } from "../../api/mentalModels";
import { findTakeaway, type UserTakeaway } from "../../api/userTakeaways";
import type { NowChanged, NowMode } from "../../api/now";
import type { DeeperItem, DeeperRoute } from "../../lib/diveDeeper";
import MmVisit, { type MmVisitEditor } from "../mentalModel/MmVisit";
import { useMmRegister } from "../../hooks/useMmText";
import MmVoiceSwitch from "../mentalModel/MmVoiceSwitch";
import BreakdownDialog from "../portal/myWorkspace/actionItems/BreakdownDialog";
import ActionToActivityDialog from "../portal/myWorkspace/actionItems/ActionToActivityDialog";
import CaptureTraitPanel from "../capture/CaptureTraitPanel";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export interface ToolOutcome {
  /** Kinds and counts only — never text (spec §3.3). */
  result?: NowChanged;
  answer?: string;
}

interface Props {
  route: DeeperRoute;
  item: DeeperItem;
  mode: NowMode;
  /** The guiding question, the prompt of "Just think it through". */
  question: string;
  onDone: (out: ToolOutcome) => void;
  /** Back to the Dive deeper screen, everything kept. */
  onBack: () => void;
  /** A tool navigated away on its own (the activity wizard's Start Blank / Run / Open in design):
   *  record the hand-off and let the sheet close, without finishing. */
  onHandoff: (route: "activity") => void;
}

/**
 * Runs an inline Dive deeper choice over the screen with today's handlers (spec 2026-11-04 §2.4):
 * the two Need help branches (BreakdownDialog, ActionToActivityDialog), the quality panel,
 * one operator in MmVisit (lifted first if needed), and the one-question screens.
 */
const NowToolHost = ({ route, item, mode, question, onDone: onDoneProp, onBack, onHandoff }: Props) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const needsStep = route.route === "breakdown" || route.route === "guided_activity";
  const needsModel = route.route === "operator";
  const [step, setStep] = useState<UserTakeaway | null>(null);
  const [object, setObject] = useState<MmObject | null>(null);
  const [loading, setLoading] = useState(needsStep || needsModel);
  const [failed, setFailed] = useState(false);
  const [answer, setAnswer] = useState("");
  const [editor, setEditor] = useState<MmVisitEditor>(null);
  const [register, setRegister] = useMmRegister();
  const finished = useRef(false);
  const created = useRef(false);
  const moved = useRef(false);
  const left = useRef(false);

  /** Reports back at most once, however many dialogs fire their callbacks. */
  const onDone = (out: ToolOutcome) => {
    if (finished.current) return;
    finished.current = true;
    onDoneProp(out);
  };

  useEffect(() => {
    if (!needsStep && !needsModel) return;
    let live = true;
    const load = async () => {
      if (needsStep) {
        const r = await findTakeaway(retryFetch, item.id);
        if (!live) return;
        setStep(r.item ?? null);
        setFailed(!r.item);
        return;
      }
      let found: MmObject | null = null;
      if (item.kind === "mental_model") {
        const { objects } = await listObjects(retryFetch, { status: "all" });
        found = objects.find((o) => o._id === item.id) ?? null;
      } else {
        const lifted = await liftEntity(retryFetch, {
          entityType: item.kind === "quality" ? "user_trait" : "user_takeaway",
          entityId: item.id,
          objectType: item.mmType,
        });
        found = lifted.object;
      }
      if (!live) return;
      setObject(found);
      setFailed(!found);
    };
    load()
      .catch(() => live && setFailed(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.route, item.id]);

  const back = (
    <Button variant="ghost" className="min-h-11" onClick={onBack}>
      <ArrowLeft className="me-1.5 h-4 w-4 rtl:rotate-180" aria-hidden />
      {t("now.back")}
    </Button>
  );

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 py-6 text-sm text-foreground/80" role="status">
          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
          {t("now.dive.opening")}
        </div>
        {back}
      </div>
    );
  }

  if (failed) {
    return (
      <div className="space-y-3">
        <p role="alert" className="text-sm text-foreground/80">{t("now.dive.failed")}</p>
        {back}
      </div>
    );
  }

  const modelResult = (): ToolOutcome => (moved.current ? { result: { kind: "model_updated" } } : {});

  switch (route.route) {
    case "breakdown":
      return (
        <div className="space-y-3">
          {back}
          <BreakdownDialog
            open
            actionItem={step}
            onSubtasksCreated={(count) => {
              created.current = true;
              onDone(count && count > 0 ? { result: { kind: "steps_added", count } } : {});
            }}
            onOpenChange={(o) => {
              if (!o && !created.current) onBack();
            }}
          />
        </div>
      );
    case "guided_activity":
      return (
        <div className="space-y-3">
          {back}
          <ActionToActivityDialog
            open
            actionItem={step}
            // The wizard shows its own success step (Run activity / Open in design), so creating
            // is only remembered; the person finishes by closing it (spec §2.3, §7).
            onActivityCreated={() => {
              created.current = true;
            }}
            onLeave={() => {
              if (left.current || finished.current) return;
              left.current = true;
              onHandoff("activity");
            }}
            onOpenChange={(o) => {
              if (o || left.current) return;
              if (created.current) onDone({ result: { kind: "activity_created" } });
              else onBack();
            }}
          />
        </div>
      );
    case "trait_panel":
      return (
        <div className="space-y-3">
          {back}
          <CaptureTraitPanel traitId={item.id} open onClose={() => onDone({})} onFailed={() => setFailed(true)} />
        </div>
      );
    case "operator":
      if (!object) return null;
      return (
        <div className="space-y-3">
          {back}
          <MmVisit
            object={object}
            // The person's chosen voice, shared with the model page (Simple / Guided / Expert).
            register={register}
            reason={{ kind: "friction", frictionType: mode === "respond" ? "external" : "internal" }}
            startOperator={route.operator}
            editor={editor}
            onEditorChange={setEditor}
            onObjectChange={() => {
              moved.current = true;
            }}
            onEnd={() => onDone(modelResult())}
          />
          <div className="flex flex-wrap items-start justify-between gap-2">
            <MmVoiceSwitch register={register} onChange={setRegister} />
            <Button variant="ghost" className="min-h-11 ms-auto" onClick={() => onDone(modelResult())}>
              {t("now.dive.doneWith")}
            </Button>
          </div>
        </div>
      );
    default: {
      const label =
        route.route === "project_step"
          ? t("now.dive.projectStepQuestion")
          : route.route === "next_step"
            ? t("now.dive.nextStepQuestion")
            : question;
      return (
        <div className="space-y-3">
          <label htmlFor="now-tool-answer" className="block text-base font-semibold text-foreground">
            {label}
          </label>
          <Textarea id="now-tool-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} maxLength={2000} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            {back}
            <div className="flex gap-2">
              <Button variant="ghost" className="min-h-11" onClick={() => onDone({})}>
                {t("now.notSure")}
              </Button>
              <Button className="rounded-full min-h-11" disabled={!answer.trim()} onClick={() => onDone({ answer: answer.trim() })}>
                {t("now.continue")}
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }
};

export default NowToolHost;
